import {
  Autocomplete,
  CircularProgress,
  Fade,
  IconButton,
  InputAdornment,
  ListItemAvatar,
  ListItemText,
  TextField,
} from '@mui/material';

import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { Clear } from '@mui/icons-material';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';
import { FileIcon } from '@/modules/files/components/FileIcon';

import { Or2ListItem } from '../data-display/Or2ListItem';

const LINK_INPUT_WITH_SEARCHER_SEARCH_RESULT = graphql(`
  fragment linkInputWithSearcherSearchResultFields on File {
    _id
    filename
    folder
    lastModified
    url
    parent {
      _id
      filename
    }
    lastModifiedBy {
      _id
      firstName
      lastName
    }
    lastUploaded
    lastUploadedBy {
      _id
      firstName
      lastName
    }
  }
`);

export type FileSearchResult = DocumentType<
  typeof LINK_INPUT_WITH_SEARCHER_SEARCH_RESULT
>;

/**
 * This component has changed substantially given MUI's Autocomplete does most of what this did natively. Props have changed
 *
 * searchResults -> options
 * onInputValueChange: (value: string | SearchResult) => void is split into ->
 *  onInputChange (value: string) => void
 *  onChange (value: FileSearchResult) => void
 * searching -> loading
 * onClearSearchResults -> no longer needed
 * invalid -> error
 */
interface Props {
  loading?: boolean;
  error?: boolean;
  options: FileSearchResult[];
  inputValue: string;
  onChange: (value: FileSearchResult) => void;
  onInputChange: (value: string) => void;
}

/** This component has changed substantially given MUI's Autocomplete does most of what this did natively. Props have changed */
export const LinkInput = ({
  loading,
  error,
  options,
  inputValue,
  onChange,
  onInputChange,
}: Props) => {
  return (
    <Autocomplete
      fullWidth
      freeSolo
      disableClearable
      options={options}
      inputValue={inputValue}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : (option.filename ?? '')
      }
      onInputChange={(_, value) => onInputChange(value)}
      onChange={(_, value) => {
        if (typeof value === 'string') return;
        onChange(value);
      }}
      renderOption={(props, option, { inputValue }) => {
        const matches = match(option.filename ?? '', inputValue, {
          insideWords: true,
        });
        const parts = parse(option.filename ?? '', matches);

        return (
          <Or2ListItem {...props} key={option._id}>
            <ListItemAvatar>
              <FileIcon file={option} />
            </ListItemAvatar>
            <ListItemText
              primary={
                <div>
                  {parts.map((part, index) => {
                    return part.highlight ? (
                      <strong key={String(index)}>{part.text}</strong>
                    ) : (
                      <span key={String(index)}>{part.text}</span>
                    );
                  })}
                </div>
              }
              secondary={
                option?.parent?.filename ? (
                  <span>
                    in &quot;
                    {option.parent.filename}
                    &quot;
                  </span>
                ) : null
              }
            />
          </Or2ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          margin="dense"
          label="Link to"
          placeholder="Enter a web address or search for a file"
          autoFocus
          error={error}
          helperText={error ? 'Please enter a valid address' : null}
          slotProps={{
            input: {
              ...params.InputProps,
              type: 'search',
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress disableShrink size={20} />
                  ) : (
                    <Fade in={!loading}>
                      <IconButton
                        disabled={inputValue === ''}
                        aria-label="Clear link input"
                        onClick={() => onInputChange('')}
                        edge="end"
                        size="large"
                      >
                        <Clear />
                      </IconButton>
                    </Fade>
                  )}
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
};
